import clsx from 'clsx';
import type { ComponentPropsWithRef, ReactNode } from 'react';

import Text, { type TextProps } from '@commons/components/Text';
import BaseContainer, { type BaseContainerProps } from '@commons/containers/BaseContainer';
import Flex, { type FlexProps } from '@commons/containers/Flex';

import styles from './index.module.scss';

// Type definition for the Card component props.

type CardProps = ComponentPropsWithRef<typeof Flex> & {
  children: ReactNode;
  hasBorder?: boolean;
  active?: boolean;
  disabled?: boolean;
};

/**
 * Card root component.
 */

const Card = (props: CardProps) => {
  const { children, active, disabled, className, hasBorder = false, ref, ...rest } = props;

  const classes = clsx(
    styles.card,
    active && 'active',
    disabled && styles.disabled,
    hasBorder && styles.border,
    'h-100',
    !disabled && 'has-nested-button',
    className
  );

  // Properties to apply border styles to the Card component if needed.

  const borderProps: Partial<FlexProps> = {
    border: hasBorder ? { xs: 'top', lg: 'left' } : undefined,
    paddingTop: hasBorder ? { xs: 'xs', lg: 'none' } : undefined,
    paddingLeft: hasBorder ? { xs: 'none', lg: 'xs' } : undefined,
  };

  return (
    <Flex
      ref={ref}
      gap="xs"
      width={12}
      direction="column"
      justify="space-between"
      className={classes}
      {...borderProps}
      {...rest}
    >
      {children}
    </Flex>
  );
};

// Card overlay sub-component.

const CardOverlay = ({ children, className, ...props }: BaseContainerProps) => (
  <BaseContainer className={clsx(styles.overlay, className)} {...props}>
    {children}
  </BaseContainer>
);

// Card root component.

const CardContent = ({ children }: CardProps) => {
  return (
    <Flex gap="xs" width={12} direction="column" className="h-100">
      {children}
    </Flex>
  );
};

// Card title sub-component.

const CardTitle = ({
  as = 'h3',
  level = 'h5',
  transform,
  marginBottom = 'none',
  children,
  className,
  ...props
}: TextProps) => {
  const classes = clsx(styles.title, className);

  return (
    <Text
      as={as}
      level={level}
      transform={transform}
      marginBottom={marginBottom}
      className={classes}
      {...props}
    >
      {children}
    </Text>
  );
};

/**
 * Card root component.
 *
 * Sets the base properties / styles for the card component.
 * Also used as a namespace for the sub-components.
 *
 * Sub-components can be used as children to reutilize their styles.
 *
 * @example
 * <Card href="/">
 *   <Card.Title>...</Card.Title>
 * </Card>
 */

const CardNamespace = Object.assign(Card, {
  /**
   * Card overlay sub-component.
   *
   * Can be used as an image wrapper to render an overlay when the card is hovered / active.
   */
  Overlay: CardOverlay,

  /**
   * Card title sub-component.
   *
   * Ensure the title has an active color when the card is hovered / active.
   */
  Title: CardTitle,

  /**
   * Card content sub-component.
   *
   * Ensure the card content is grouped together and separated from the Button
   */
  Content: CardContent,
});

export { CardNamespace as Card };

export default CardNamespace;
